import {toFloat} from 'helpers/number-helper';
import Const from 'const';

interface ProjOwnValueStructure {
    admin: number;
    user: number;
    isUserValueSetByUser: boolean;
}

interface ModeProjOwnDataStructure {
    overall: number;
    modes: {[modeName: string]: ProjOwnValueStructure};
}

export interface PlayerProjOwnInterface {
    data: any;
    getValue(modeName?: string): number;
    setValueFromPlayerPayload(data): void;
    setValueFromCsv(data): void;
    setValueFromStorage(data): void;
    getValueToSaveInStorage(): object;
    resetValue(): void;
}

const projOwn = {
    calcOverall: (data: ModeProjOwnDataStructure) => {
        return Object.values(data.modes)
            .map((value) => value.user)
            .reduce((a, b) => a + b);
    },
    normalizeValue: (value) => toFloat(value, 2),
};

const playerProjOwn = {
    data: {
        admin: 0,
        user: 0,
        isUserValueSetByUser: false,
    } as ProjOwnValueStructure,
    getValue(): number {
        return this.data.user;
    },
    setValueFromPlayerPayload(data): void {
        const value = projOwn.normalizeValue(data.projected_ownership);

        if (!this.data.isUserValueSetByUser) {
            this.data.user = value;
        }

        this.data.admin = value;
    },
    setValueFromCsv(data): void {
        Object.keys(data).map((propName) => {
            if (propName === 'projected_ownership') {
                this.data.user = projOwn.normalizeValue(data[propName]);
                this.data.isUserValueSetByUser = true;
            }
        });
    },
    setValueFromStorage(data): void {
        this.data.user = projOwn.normalizeValue(data.value);
        this.data.isUserValueSetByUser = true;
    },
    getValueToSaveInStorage(): object {
        return {value: this.data.user};
    },
    resetValue(): void {
        this.data.user = this.data.admin;
        this.data.isUserValueSetByUser = false;
    },
} as PlayerProjOwnInterface;

const playerModeProjOwn = {
    data: {
        overall: 0,
        modes: {},
    } as ModeProjOwnDataStructure,
    getValue(modeName: string = 'flex'): number {
        if (modeName === Const.OVERALL_MODE) {
            return this.data?.overall || 0;
        }

        return this.data.modes[modeName.toLowerCase()]?.user || 0;
    },
    setValueFromPlayerPayload(data): void {
        const propNameToModeNameMap = {
            projected_ownership: 'flex',
            projected_ownership_cpt: 'cpt',
            projected_ownership_star: 'star',
            projected_ownership_pro: 'pro',
            projected_ownership_flex: 'flex',
        };

        Object.keys(propNameToModeNameMap).map((propName) => {
            if (!(propName in data) || !(propNameToModeNameMap[propName] in this.data.modes)) {
                return;
            }

            const value = projOwn.normalizeValue(data[propName]);

            if (!this.data.modes[propNameToModeNameMap[propName]].isUserValueSetByUser) {
                this.data.modes[propNameToModeNameMap[propName]].user = value;
            }

            this.data.modes[propNameToModeNameMap[propName]].admin = value;
        });

        this.data.overall = projOwn.normalizeValue(projOwn.calcOverall(this.data));
    },
    setValueFromCsv(data): void {
        const propNameToModeNameMap = {
            projected_ownership: 'flex',
            captain_projected_ownership: 'cpt',
            star_projected_ownership: 'star',
            pro_projected_ownership: 'pro',
        };

        Object.keys(data).map((propName) => {
            if (propName in propNameToModeNameMap) {
                this.data.modes[propNameToModeNameMap[propName]].user = projOwn.normalizeValue(data[propName]);
                this.data.modes[propNameToModeNameMap[propName]].isUserValueSetByUser = true;
            }
        });

        this.data.overall = projOwn.normalizeValue(projOwn.calcOverall(this.data));
    },
    setValueFromStorage(data): void {
        const modeNames = Object.keys(this.data.modes);

        Object.keys(data).map((modeName) => {
            if (inArray(modeName, modeNames)) {
                this.data.modes[modeName].user = projOwn.normalizeValue(data[modeName].value);
                this.data.modes[modeName].isUserValueSetByUser = true;
            }
        });

        this.data.overall = projOwn.normalizeValue(projOwn.calcOverall(this.data));
    },
    getValueToSaveInStorage(): object {
        const {modes} = this.data;

        const projOwns = {};
        Object.keys(modes).map((modeName) => {
            projOwns[modeName] = {value: modes[modeName].user};
        });

        return projOwns;
    },
    resetValue(): void {
        Object.keys(this.data.modes).map((key) => {
            this.data.modes[key].user = this.data.modes[key].admin;
            this.data.modes[key].isUserValueSetByUser = false;
        });

        this.data.overall = projOwn.normalizeValue(projOwn.calcOverall(this.data));
    },
} as PlayerProjOwnInterface;

export function getPlayerProjOwnFunctionality(player): PlayerProjOwnInterface {
    const data = {admin: 0, user: 0, isUserValueSetByUser: false};

    const projOwn = {
        ...playerProjOwn,
        data,
    };

    projOwn.setValueFromPlayerPayload(player);

    return projOwn;
}

export function getPlayerModeProjOwnFunctionality(player, modeNames: string[]): PlayerProjOwnInterface {
    const availableModeNames = ['cpt', 'star', 'pro', 'flex'];

    const data = {overall: 0, modes: {}};

    modeNames
        .filter((modeName) => inArray(modeName, availableModeNames))
        .map((modeName) => (data.modes[modeName] = {admin: 0, user: 0, isUserValueSetByUser: false}));

    const projOwn = {
        ...playerModeProjOwn,
        data,
    };

    projOwn.setValueFromPlayerPayload(player);

    return projOwn;
}
